import React from 'react';
import { styled, Box } from '@mui/material';
import clsx from 'clsx';
import { Grid2 } from '../../Mui/Grid2';
import { IconInfoOutline } from '../../internal/icons';
import { Button } from '../../Button';
import { Tooltip } from '../../Tooltip';
import { getStyle, formItemClasses } from './style';
import { useTranslation } from '../../hooks/useTranslation';
function toTooltipProps(tooltip) {
    if (!tooltip) {
        return null;
    }
    if (typeof tooltip === 'object' && !React.isValidElement(tooltip)) {
        return tooltip;
    }
    return {
        title: tooltip,
    };
}
export function FormItemNoContextLabelBase(props) {
    const { className, label, labelWidth, labelCol, labelAlign, colon, htmlFor, required, requiredMark, tooltip, labelExtra, description, layout, labelWrap, } = props;
    const { t } = useTranslation('Form');
    if (!label) {
        return null;
    }
    const mergedLabelCol = labelCol ?? {};
    const mergedLabelAlign = labelAlign ?? 'left';
    let labelChildren = label;
    let requireMarkNode = null;
    const computedColon = colon === true || colon !== false;
    const haveColon = computedColon && layout !== 'vertical';
    if (haveColon && typeof label === 'string' && label.trim() !== '') {
        labelChildren = label.replace(/[:|：]\s*$/, '');
    }
    if (requiredMark !== false) {
        if (!required && requiredMark === 'optional') {
            requireMarkNode = (React.createElement("span", { className: formItemClasses.label.optionalMark },
                "(",
                t('optional'),
                ")"));
        }
        if (required) {
            requireMarkNode = (React.createElement("span", { className: formItemClasses.label.requiredMark, "aria-hidden": true }, "*"));
        }
    }
    // Tooltip
    let tooltipNode = null;
    const tooltipProps = toTooltipProps(tooltip);
    if (tooltipProps) {
        const { icon = React.createElement(IconInfoOutline, null), ...restTooltipProps } = tooltipProps;
        tooltipNode = (React.createElement(Tooltip, { ...restTooltipProps, arrow: true },
            React.createElement(Button, { variant: "icon", type: "tertiary", size: "small", "aria-label": typeof label === 'string'
                    ? t('learnMoreWithName', { name: `${label}` })
                    : t('learnMore'), role: "button", sx: {
                    minWidth: '20px !important',
                    width: '20px !important',
                    height: '20px !important',
                }, className: formItemClasses.label.tooltip }, icon)));
    }
    const labelClassName = clsx(className, formItemClasses.label.label, {
        [`${formItemClasses.label.required}`]: required,
        [`${formItemClasses.label.optional}`]: !required && requiredMark !== false,
    });
    const labelColClassName = clsx(formItemClasses.label.root, mergedLabelAlign === 'left' && `${formItemClasses.label.left}`, mergedLabelCol.className, {
        [`${formItemClasses.label.wrap}`]: !!labelWrap,
    });
    return (React.createElement(Grid2, { ...mergedLabelCol, className: labelColClassName },
        React.createElement(Box, { className: clsx(formItemClasses.label.container) },
            React.createElement(Box, { className: clsx(formItemClasses.label.wrapper, {
                    [`${formItemClasses.label.colon}`]: haveColon,
                }) },
                React.createElement("label", { htmlFor: htmlFor, className: labelClassName, style: { width: labelWidth } }, labelChildren),
                requireMarkNode,
                tooltipNode),
            labelExtra ? React.createElement(Box, null, labelExtra) : null),
        description && layout === 'vertical' ? (React.createElement(Box, { className: clsx(formItemClasses.label.description), id: `${htmlFor}-desc` }, description)) : null));
}
const StyledFormItemNoContextLabel = styled(FormItemNoContextLabelBase)((props) => {
    const { theme } = props;
    return getStyle(theme);
});
export const FormItemNoContextLabel = React.forwardRef((props, ref) => (React.createElement(StyledFormItemNoContextLabel, { ...props, ref: ref })));
